<template>
    <div class="page-wrap">
        <div v-if="!hideBar && !blocked" class="page-bar" :class="{ 'playbook-has-ai': !previewNode && canEditNodes && node.Type == 'NODE' && node.Slug != 'home' }">
            <div v-if="isContentNode" class="pb-detail">
                <div class="page-bar-sub">
                    <span v-if="previewNode">Preview · not saved</span>
                    <a v-else class="pb-crumb" @click.prevent="goBack()">
                        <svg viewBox="0 0 24 24"><path d="M19 12H5M11 18l-6-6 6-6" /></svg>{{ typeCrumb }}
                    </a>
                </div>
                <div class="pb-title">
                    <span class="page-bar-main"><ai-forge-name :name="node.Name" /></span>
                    <template v-if="!previewNode">
                        <!-- Unpublished (Draft / Needs Approval / Inactive). Standard users only get here through a share link: the badge tells them it isn't live -->
                        <span v-if="node.NeedsApproval" class="dbadge st-draft">{{ node.NeedsApprovalType || 'Draft' }}</span>
                        <span v-else-if="node.NeedsReview && canMarkReviewed" class="dbadge st-review">Needs review</span>
                        <!-- Saves this node to the menu favorites as 'node:<slug>' (same key Nodes.vue uses) -->
                        <button type="button" :class="isFavorite ? 'page-bar-fav on' : 'page-bar-fav'" :title="isFavorite ? 'Remove from favorites' : 'Add to favorites'" @click="toggleFavorite()">
                            <svg-icon :icon="isFavorite ? 'starfilled' : 'star'" />
                        </button>
                    </template>
                </div>
                <div class="page-bar-owner">{{ metaLine }}</div>
            </div>
            <div v-else>
                <div class="page-bar-sub">
                    <span v-if="previewNode">Preview · not saved</span>
                    <span v-else-if="node.Slug == 'home' && $store.state.user.firstName">Welcome back, {{ $store.state.user.firstName }}</span>
                    <span v-else>Foundations</span>
                </div>
                <div class="page-bar-main">
                    <span>{{ node.Name }}</span>
                </div>
            </div>
            <div v-if="previewNode" style="display:flex;align-items:center">
                <button type="button" class="btn-light" @click="$emit('close-preview')">Close Preview</button>
            </div>
            <!-- Detail actions: one secondary, one primary, everything else in the menu -->
            <div v-else-if="isContentNode" class="pb-actions">
                <button v-if="node.Type == 'TIMELINE' && timeline" type="button" class="btn-light pb-btn" @click="presenting = true">
                    <svg viewBox="0 0 24 24"><path d="M3 4h18M4 4v11a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1V4M12 16v4M8 20h8" /></svg><span>Present</span>
                </button>
                <!-- One click to the editor with Improve with AI already open (Nate, 3 Nov: Edit first was an extra step) -->
                <button v-if="canImprove" type="button" class="ai-btn pb-btn" @click="improveWithAI()">
                    <svg viewBox="0 0 24 24" style="fill: currentColor; stroke: none" aria-hidden="true"><path d="M13 0L3.5 14H10l-1.5 10L20 9h-6.5L15 0h-2z"/></svg><span>Improve with AI</span>
                </button>
                <button v-if="$store.state.user.isAdmin" type="button" class="btn-light pb-btn" @click="showWhoCanView = true">
                    <svg viewBox="0 0 24 24"><path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12z" /><circle cx="12" cy="12" r="3" /></svg><span>Who Can View</span>
                </button>
                <div class="pb-more">
                    <button type="button" class="pb-kebab" aria-haspopup="menu" :aria-expanded="showDetailMenu" aria-label="More actions" @click="showDetailMenu = !showDetailMenu">
                        <svg-icon icon="dots" />
                    </button>
                    <div v-if="showDetailMenu" class="pb-menu" role="menu" @click="showDetailMenu = false">
                        <template v-if="canMarkReviewed || (canEditNodes && node.Slug != 'home')">
                            <template v-if="canMarkReviewed">
                                <button v-if="reviewedToday" type="button" class="pb-mi disable-button" role="menuitem" disabled>
                                    <svg viewBox="0 0 24 24"><path d="M5 12.5l4.5 4.5L19 7.5" /></svg>Reviewed today
                                </button>
                                <button v-else type="button" class="pb-mi" role="menuitem" @click="markReviewed()">
                                    <svg viewBox="0 0 24 24"><path d="M5 12.5l4.5 4.5L19 7.5" /></svg>Mark reviewed
                                </button>
                            </template>
                            <button v-if="canEditNodes && editRoute" type="button" class="pb-mi" role="menuitem" @click="$router.push(editRoute)">
                                <svg viewBox="0 0 24 24"><path d="M4 20h4L18.5 9.5a2.83 2.83 0 0 0-4-4L4 16v4M13.5 6.5l4 4" /></svg>Edit
                            </button>
                            <button v-if="canEditNodes && node.Slug != 'home'" type="button" class="pb-mi" role="menuitem" @click="editDetails()">
                                <svg viewBox="0 0 24 24"><path d="M4 6h10M18 6h2M4 12h4M12 12h8M4 18h12" /><circle cx="16" cy="6" r="2" /><circle cx="10" cy="12" r="2" /><circle cx="18" cy="18" r="2" /></svg>Edit Details
                            </button>
                            <div class="pb-sep"></div>
                        </template>
                        <button type="button" class="pb-mi" role="menuitem" @click="sharePDF()">
                            <svg viewBox="0 0 24 24"><circle cx="6" cy="12" r="3" /><circle cx="18" cy="6" r="3" /><circle cx="18" cy="18" r="3" /><path d="M8.7 10.7l6.6-3.4M8.7 13.3l6.6 3.4" /></svg>Share
                        </button>
                        <template v-if="node.Type != 'VIDEO'">
                            <button type="button" :class="gettingPDF ? 'pb-mi disable-button' : 'pb-mi'" role="menuitem" @click="getPDF(false)">
                                <svg viewBox="0 0 24 24"><path d="M14 3v4a1 1 0 0 0 1 1h4M17 21H7a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h7l5 5v11a2 2 0 0 1-2 2zM12 11v6M9 14l3 3 3-3" /></svg>Download PDF
                            </button>
                            <button v-if="node.Type == 'CHECKLIST'" type="button" :class="gettingPDF ? 'pb-mi disable-button' : 'pb-mi'" role="menuitem" @click="getPDF(true)">
                                <svg viewBox="0 0 24 24"><path d="M14 3v4a1 1 0 0 0 1 1h4M17 21H7a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h7l5 5v11a2 2 0 0 1-2 2zM9 13h6M9 17h6" /></svg>PDF w/ Lines
                            </button>
                        </template>
                        <button v-if="$store.state.user.isAdmin" type="button" :class="gettingPDF ? 'pb-mi disable-button' : 'pb-mi'" role="menuitem" @click="getQRPDF()">
                            <svg viewBox="0 0 24 24"><path d="M4 4h6v6H4zM14 4h6v6h-6zM4 14h6v6H4zM14 14h2v2h-2zM18 14h2M14 18v2M18 18h2v2h-2" /></svg>QR code
                        </button>
                        <template v-if="$store.state.user.isAdmin && node.Type != 'VIDEO'">
                            <div v-if="canEditNodes" class="pb-sep"></div>
                            <button v-if="canEditNodes" type="button" class="pb-mi" role="menuitem" @click="duplicateNode()">
                                <svg viewBox="0 0 24 24"><path d="M9 9h10a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H11a2 2 0 0 1-2-2V9zM5 15H4a1 1 0 0 1-1-1V5a2 2 0 0 1 2-2h9a1 1 0 0 1 1 1v1" /></svg>Duplicate
                            </button>
                            <button v-if="canImprove" type="button" class="pb-mi" role="menuitem" @click="improveWithAI()">
                                <svg viewBox="0 0 24 24" class="pb-mi-fill"><path d="M13 0L3.5 14H10l-1.5 10L20 9h-6.5L15 0h-2z"/></svg>Improve with AI
                            </button>
                        </template>
                    </div>
                </div>
            </div>
            <template v-else>
                <button v-if="canEditNodes && node.Type == 'NODE' && node.Slug != 'home'" type="button" class="ai-btn playbook-ai-launcher" :title="'Make something in ' + node.Name + ' with The Forge'" @click="showAICreate = true"><svg aria-hidden="true" xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="currentColor" fill-rule="evenodd"><path d="M12 23c-4.4 0-8-3.1-8-7.5 0-3.3 2-5.6 3.6-7.4.4-.4 1.1-.1 1.1.5 0 1.3.6 2.6 1.6 3.2C10.1 7.7 11.6 4 15 1.2c.4-.3 1 0 1 .5-.1 2.6.9 4.5 2.2 6.4C19.3 9.8 20 11.6 20 15.5 20 19.9 16.4 23 12 23zm0-2c1.9 0 3.3-1.3 3.3-3.1 0-1.6-.9-2.7-2.1-3.9-.3-.3-.8-.1-.8.3-.1 1-.6 1.8-1.3 2.2-.1-1.1-.5-2-1.1-2.6-.3-.3-.8-.1-.8.3-.3 1.3-1.5 2.2-1.5 3.7C7.7 19.7 9.8 21 12 21z"/></svg>Forge here</button>
                <div style="display:flex;align-items:center" class="hide-mobile-991">
                    <div v-if="node.Slug != 'home'" class="btn-light" style="margin-right:15px" @click="$router.back()">Back</div>
                </div>
            </template>
        </div>
        <!-- A link to something you can't open (403) or that's gone (404): why, in place of the page -->
        <div v-if="blocked" class="pb-blocked slide-up" role="alert">
            <svg v-if="blocked.status == 403" viewBox="0 0 24 24" aria-hidden="true"><rect x="4" y="11" width="16" height="10" rx="2" /><path d="M8 11V7a4 4 0 0 1 8 0v4" /></svg>
            <svg v-else viewBox="0 0 24 24" aria-hidden="true"><circle cx="11" cy="11" r="7" /><path d="M21 21l-5-5" /></svg>
            <h2>{{ blocked.status == 403 ? 'You Don\'t Have Access' : 'Page Not Found' }}</h2>
            <p>{{ blocked.message }}</p>
            <div class="pb-blocked-btns">
                <button v-if="canGoBack" type="button" class="btn-light" @click="$router.back()">Back</button>
                <router-link to="/playbook/home" class="btn">Go to Home</router-link>
            </div>
        </div>
        <div v-else :class="$store.state.routeLoaded ? 'slide-up view' : 'hide'">
            <div v-if="gettingPDF" class="pb-pdf-status">Preparing PDF…</div>
            <div :class="hasRail ? 'playbook-' + node.Type + ' playbook-with-rail' : 'playbook-' + node.Type" style="display:flex;justify-content:center">
                <div :class="node.Type == 'NODE' ? '' : 'playbook'" :style="node.Type == 'NODE' ? 'width:1160px;max-width:100%' : ''">
                    <div v-if="node.Slug == 'home' && $store.state.user.teamHubEmployeeLoaded && $store.state.user.teamHubEmployee">
                        <forge-daily-digest />
                        <employee-home-node />
                    </div>
                    <div v-if="$store.state.adminRolesCanEdit.includes($store.state.user.role) && node.Type == 'NODE' && node.Slug != 'home'" class="page-filters pf-bar">
                        <div class="add-btn" title="Add manually" @click="addEditNode(null)">Add New</div>
                        <div class="add-btn" @click="() => { showAddExisting = true }">Add Existing</div>
                    </div>
                    <div v-if="nextNodes.length">
                        <div v-if="!showReorder" class="next-nodes-grid">
                            <div v-for="(next, i) in nextNodes" :key="i" class="next-node-card-wrap">
                                <div class="next-node-card" @click="handleNode(next)">
                                    <div class="next-node-card-top">
                                        <div class="next-node-card-name">{{ next.Name }}</div>
                                        <button v-if="$store.state.user.isAdmin" type="button" class="next-node-menu-btn" @click.stop="toggleNextNodeMenu(next, $event)">
                                            <svg-icon icon="dots" />
                                        </button>
                                    </div>
                                    <div v-if="next.nextNodes.length" class="next-node-card-sub">{{ next.nextNodes.length }} subnode{{ next.nextNodes.length == 1 ? '' : 's' }}</div>
                                </div>
                                <div v-if="openNextNodeSlug == next.Slug" class="next-node-menu-dropdown" :style="nextNodeMenuStyle" @click.stop>
                                    <template v-if="canEditNodes">
                                        <a @click.prevent="nextNodeMenuAction(() => addEditNode(next))">Edit Node</a>
                                        <router-link v-if="next.Type == 'PROCESS'" :to="'/edit-process/' + next.Slug" @click="closeNextNodeMenu">Edit Process</router-link>
                                        <router-link v-if="next.Type == 'TIMELINE'" :to="'/edit-timeline/' + next.Slug" @click="closeNextNodeMenu">Edit Value Engine</router-link>
                                        <router-link v-if="next.Type == 'SHEET'" :to="'/edit-sheet/' + next.Slug" @click="closeNextNodeMenu">Edit Sheet</router-link>
                                        <a @click.prevent="nextNodeMenuAction(() => removeNode(next))">Remove from {{ node.Name }}</a>
                                        <div class="row-menu-divider"></div>
                                    </template>
                                    <div class="next-node-menu-info">
                                        <span>{{ $store.state.playbookNodeTypeMap[next.Type] }}</span>
                                        <span v-for="(location, k) in next.Locations" :key="k">{{ location }}</span>
                                        <span v-if="next.NeedsApproval" style="color:red">NEEDS APPROVAL</span>
                                        
                                    </div>
                                    <div v-if="next.nextNodes.length" class="next-nodes-preview">
                                        <div class="row-menu-divider"></div>
                                        <a v-for="(nextNext, j) in next.nextNodes" :key="j" @click.prevent="handleNode(nextNext)">{{ nextNext.Name }}</a>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div v-if="$store.state.adminRolesCanEdit.includes($store.state.user.role)">
                            <div v-if="showReorder">
                                <draggable v-model="nextNodes" class="next-nodes">
                                    <div v-for="(next, i) in nextNodes" :key="i">
                                        <div class="btn">{{ next.Name }}</div>
                                    </div>
                                </draggable>
                            </div>
                            <div style="margin-top:25px">
                                <span @click="toggleShowReorder()" style="cursor: pointer; text-decoration: underline;">
                                    <span v-if="showReorder">Save</span>
                                    <span v-else>Reorder</span>
                                </span>
                            </div>
                        </div>
                        <div v-if="node.Slug == 'home' && $store.state.user.teamHubEmployeeLoaded" style="margin-top:24px">
                            <announcements-home-node />
                        </div>
                    </div>
                    <div v-else-if="process" class="playbook-processes">
                        <process-header :node="node" :location-string="locationString" :departments-string="departmentsString" :node-types-string="nodeTypesString" :last-modified="lastModified" :last-reviewed="lastReviewed" :prev-nodes="prevNodes" :files="files" :link-target="previewNode ? '_blank' : null" />
                        <!-- ProcessLayout (tblNode, set in EditProcess): 'grid' puts each step in a box, three to a row (style.scss .playbook-steps-grid).
                             The groups come from processStepGroups (the grid's first step sits full width above it) -->
                        <div v-for="(group, g) in processStepGroups" :key="g" :class="['playbook-steps', group.layout ? 'playbook-steps-' + group.layout : '']">
                            <div v-for="{ item, i } in group.steps" :key="i" :class="['playbook-process', { 'playbook-process-hide-line': item.hideSectionLine, 'playbook-process-changed': changedSteps && changedSteps.includes(i) }]">
                                <h3>{{ item.header }}</h3>
                                <div v-if="item.body" v-html="item.body" style="text-align:left"></div>
                                <div v-if="item.image || item.image2" :class="(item.image && item.image2) ? 'playbook-process-images playbook-process-multi-images' : 'playbook-process-images'">
                                    <div v-if="item.image"><img :src="item.image" /></div>
                                    <div v-if="item.image2"><img :src="item.image2" /></div>
                                </div>
                                <div v-if="item.video" class="video-wrap" style="width: 100%">
                                    <div class="video">
                                        <video v-if="item.video.includes('.mp4') || item.video.includes('.webm') || item.video.includes('.ogg')" allowfullscreen="1" autostart="0" controls>
                                            <source :src="item.video">
                                        </video>
                                        <iframe v-else :src="item.video" allowfullscreen="true" autostart="0" />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div v-else-if="timeline">
                        <process-header :node="node" :location-string="locationString" :departments-string="departmentsString" :node-types-string="nodeTypesString" :last-modified="lastModified" :last-reviewed="lastReviewed" :prev-nodes="prevNodes" :files="files" :link-target="previewNode ? '_blank' : null" />
                        <value-engine-view :items="timeline.items || []" :changed-steps="changedSteps" @open-link="timelineLink($event)" @open-image="openImagePop($event)" />
                    </div>
                    <div v-else-if="sheet">
                        <process-header :node="node" :location-string="locationString" :departments-string="departmentsString" :node-types-string="nodeTypesString" :last-modified="lastModified" :last-reviewed="lastReviewed" :prev-nodes="prevNodes" :files="files" :link-target="previewNode ? '_blank' : null" />
                        <role-sheet-view :sheet="sheet" :initial-seat-id="initialSeatId" :actions="!previewNode" :is-admin="$store.state.user.isAdmin" @sign="signSeat($event)" @share="sharePDF($event.id)" @open-link="timelineLink($event)" />
                    </div>
                    <div v-else-if="node.Type == 'VIDEO'">
                        <process-header :node="node" :location-string="locationString" :departments-string="departmentsString" :node-types-string="nodeTypesString" :last-modified="lastModified" :last-reviewed="lastReviewed" :prev-nodes="prevNodes" :files="files" :link-target="previewNode ? '_blank' : null" />
                        <div class="video-wrap">
                            <div class="video">
                                <video v-if="node.Link.includes('.mp4') || node.Link.includes('.webm') || node.Link.includes('.ogg')" allowfullscreen="1" autostart="0" controls>
                                    <source :src="node.Link">
                                </video>
                                <iframe v-else :src="node.Link" allowfullscreen="true" autostart="0" />
                            </div>
                        </div>
                    </div>
                    <div v-else-if="checklist" class="playbook-processes">
                        <!--
                        <process-header :node="node" :location-string="locationString" :departments-string="departmentsString" :node-types-string="nodeTypesString" :last-modified="lastModified" :last-reviewed="lastReviewed" :prev-nodes="prevNodes" :files="files" :link-target="previewNode ? '_blank' : null" />
                        -->
                        <div style="margin: 0 0 25px 0">
                            <checklist :checklistProp="checklist" :slug="node.Slug" />
                        </div>
                    </div>
                    <div v-if="node.Slug == 'home'">
                        <nodes-home-node />
                    </div>
                </div>
                <node-admin-component v-if="hasRail" :slug="node.Slug" :nodeName="node.Name" :node="node" @files-changed="getFiles()">
                    <button v-if="!$store.state.user.isAdmin" type="button" class="rz-after-btn" @click="showSuggestion = true"><svg viewBox="0 0 24 24" aria-hidden="true"><path d="M9 18h6M10 21h4M12 3a6 6 0 0 0-3.5 10.9c.6.4 1 1.1 1 1.8V16h5v-.3c0-.7.4-1.4 1-1.8A6 6 0 0 0 12 3z" /></svg>Suggest an Improvement</button>
                </node-admin-component>
            </div>
        </div>

        <div v-if="showColSignOff" class="error-msg-wrap">
            <div class="form-wrapper slide-up">
                <div class="form-close">
                    <button type="button" class="form-close-x" title="Close" @click="() => { showColSignOff = false }">&times;</button>
                </div>
                <div style="height: 100px;border-bottom:1px solid black;margin:0 10px 10px"><VueSignaturePad :ref="'signOffPad'" /></div>
                <div style="padding: 0 10px 10px"><b>Sign Name Above</b> - <a @click.prevent="() => { ($refs as any).signOffPad.clearSignature() }">Clear</a></div>
                <input style="margin-top:15px" type="text" placeholder="Type your name" v-model="colSignOffInput" />
                <input :class="colSignOffSubmitting ? 'disable-button' : ''" type="submit" value="Submit" @click="submitColSignOff()" />
            </div>
        </div>

        <!-- Present: the value engine full screen, over all the navigation. Image and page popups still
             open on top of it (.error-msg-wrap sits higher). Closes with the X or Esc. -->
        <div v-if="presenting && timeline" class="ve-present" role="dialog" :aria-label="node.Name">
            <div class="ve-present-bar">
                <span class="ve-present-title">{{ node.Name }}</span>
                <button type="button" class="ve-present-close" title="Close (Esc)" aria-label="Close" @click="presenting = false">
                    <svg viewBox="0 0 24 24"><path d="M6 6l12 12M18 6L6 18" /></svg>
                </button>
            </div>
            <div class="ve-present-body">
                <value-engine-view :items="timeline.items || []" @open-link="timelineLink($event)" @open-image="openImagePop($event)" />
            </div>
        </div>

        <div v-if="showImagePop" class="error-msg-wrap">
            <div class="form-wrapper slide-up" style="width:auto">
                <div class="form-close">
                    <button type="button" class="form-close-x" title="Close" @click="() => { showImagePop = false }">&times;</button>
                </div>
                <div style="text-align:center"><img style="max-width:1000px;width:100%" :src="imagePopURL" /></div>
            </div>
        </div>

        <div v-if="showSharePDF" class="error-msg-wrap">
            <div class="form-wrapper slide-up">
                <div class="form-close">
                    <button type="button" class="form-close-x" title="Close" @click="() => { showSharePDF = false }">&times;</button>
                </div>
                <div style="margin-bottom:15px;text-decoration:underline"><a @click.prevent="copyLink()">Copy Link</a></div>
                <h3>Email</h3>
                <input type="text" v-model="shareData.email" placeholder="Email" />
                <h3>Phone</h3>
                <phone-input v-model="shareData.phone" placeholder="Phone" />
                <div class="checkbox-input" v-if="checklist">
                    <input type="checkbox" v-model="shareData.wDescriptions" />
                    <h3>w/ Lines</h3>
                </div>
                <input :class="gettingPDF ? 'disable-button' : ''" type="submit" value="Send" @click="getPDF(false, true)" />
            </div>
        </div>

        <who-can-view v-if="showWhoCanView" :slug="node.Slug" @close="() => { showWhoCanView = false }" />
        <pdf-pop-up v-if="showPDF" :url="PDFUrl" @close="() => showPDF = false" />
        <ai-create-node v-if="showAICreate" node-type="process" :from-slug="node.Slug == 'home' ? null : node.Slug" @close="closeAICreate()" />
        <add-edit-node v-if="showAddEdit" :node="nodeToEdit" :fromSlug="duplicatingNode ? null : node.Slug" :cloneNode="duplicatingNode" @close="closeAddEdit()" />
        <add-existing-node v-if="showAddExisting" :fromSlug="node.Slug" @close="closeAddExisting()" />
        <confirm-delete v-if="showConfirmDelete" @confirm="deleteNodeConfirm" @cancel="() => { showConfirmDelete = false; }" msg="Are you sure you would like to delete this item?"/>
        <confirm-delete v-if="showConfirmRemove" @confirm="removeNodeConfirm" @cancel="() => { showConfirmRemove = false; }" :msg="'Are you sure you would like to remove this item from ' + node.Name + '?'"/>
        <process-suggestion-form v-if="showSuggestion" :nodeName="node.Name" :slug="node.Slug" @close="showSuggestion = false" />
    </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import axios from 'axios';
import PhoneInput from '@/components/PhoneInput.vue';
import { normalizePhone, PHONE_ERROR } from '@/phone';
import ProcessSuggestionForm from '@/components/ProcessSuggestionForm.vue';
import AddEditNode from '@/components/AddEditNode.vue';
import AICreateNode from '@/components/AICreateNode.vue';
import AddExistingNode from '@/components/AddExistingNode.vue';
import ConfirmDelete from '@/components/ConfirmDelete.vue';
import PdfPopUp from '@/components/PDFPopUp.vue';
import { VueDraggableNext } from 'vue-draggable-next'
import NodeAdminComponent from '@/components/NodeAdminComponent.vue';
import Checklist from '@/components/Checklist.vue';
import ProcessHeader from '@/components/ProcessHeader.vue';
import ValueEngineView from '@/components/ValueEngineView.vue';
import RoleSheetView from '@/components/RoleSheetView.vue';
import { readRoleSheet, seatSnapshot } from '@/roleSheet';
import SvgIcon from '@/components/SvgIcon.vue';
import { canUseForge } from '@/ai/forgeAccess';
import EmployeeHomeNode from '@/components/EmployeeHomeNode.vue';
import AnnouncementsHomeNode from '@/components/AnnouncementsHomeNode.vue';
import WhoCanView from '@/components/WhoCanView.vue';
import NodesHomeNode from '@/components/NodesHomeNode.vue';
import ForgeDailyDigest from '@/components/ForgeDailyDigest.vue';
import AIForgeName from '@/components/AIForgeName.vue';

export default defineComponent({
    name: 'PlaybookView',
    components: {
        AiForgeName: AIForgeName,
        PhoneInput,
        AddEditNode, AiCreateNode: AICreateNode, AddExistingNode, ConfirmDelete, PdfPopUp, NodeAdminComponent, ProcessSuggestionForm,
        draggable: VueDraggableNext, Checklist, RoleSheetView,
        ProcessHeader, ValueEngineView, SvgIcon, 
        EmployeeHomeNode, AnnouncementsHomeNode, WhoCanView, NodesHomeNode, ForgeDailyDigest
    },
    // previewNode: an unsaved node from an edit page (EditProcess/EditSheet/EditTimeline). When set, it's
    // rendered instead of fetching by route, and actions that act on the saved version are hidden.
    // hideBar: the edit page shows its own page-bar above the preview. changedSteps: process item
    // indexes that differ from the saved version, marked in the preview
    props: [ 'previewNode', 'hideBar', 'changedSteps' ],
    emits: [ 'close-preview' ],
    data() {
        return {
            node: {} as any,
            blocked: null as any, // { status: 403 | 404, message } when the node can't be opened
            canGoBack: false,
            nextNodes: [] as any,
            prevNodes: [] as any,
            showAddEdit: false,
            duplicatingNode: false,
            editingDetails: false, // Edit Details on this page's own node (refreshes the header after)
            showAICreate: false,
            showSuggestion: false,
            nodeToEdit: null,
            nodeToDelete: null,
            showConfirmDelete: false,
            nodeToRemove: null as any,
            showConfirmRemove: false,
            showAddExisting: false,
            process: null as any,
            timeline: null as any,
            sheet: null as any,
            checklist: null as any,
            lastModified: '',
            lastReviewed: '',
            gettingPDF: false,
            showPDF: false,
            PDFUrl: '',
            locations: [] as any,
            locationString: '',
            departments: [] as any,
            departmentsString: '',
            nodeTypes: [] as any,
            nodeTypesString: '',
            showReorder: false,
            files: [] as any,
            showAdminComponent: true,
            colSignOffSubmitting: false,
            colSignOffInput: '',
            colSignOffCells: null as any,
            showColSignOff: false,
            // Role sheet seat to open first (?col= link, or the viewer's own seat)
            initialSeatId: null as any,
            imagePopURL: '',
            showImagePop: false,
            shareData: {
                email: '',
                phone: '',
                wDescriptions: false,
                roleSheetCol: null as any
            },
            showSharePDF: false,
            openNextNodeSlug: null as any,
            nextNodeMenuStyle: {} as any,
            showWhoCanView: false,
            showDetailMenu: false,
            presenting: false // a value engine's full screen Present view
        }
    },
    computed: {
        isContentNode(): boolean {
            return [ 'PROCESS', 'TIMELINE', 'SHEET', 'CHECKLIST', 'VIDEO' ].includes(this.node.Type);
        },
        isFavorite(): boolean {
            return this.$store.state.navFavorites.includes('node:' + this.node.Slug);
        },
        canMarkReviewed(): boolean {
            return this.$store.state.user.role == 'Admin'
                || (this.node.ProcessOwners || []).some((o: any) => o.UserID == this.$store.state.user.userID);
        },
        canEditNodes(): boolean {
            return this.$store.state.adminRolesCanEdit.includes(this.$store.state.user.role);
        },
        // Process steps as containers of { item, i } (i = the step's index, for changedSteps). The grid layout's first step
        // is the intro: full width in the normal list style, with the grid starting at the second step (same in the PDF)
        processStepGroups(): { layout: string | null, steps: { item: any, i: number }[] }[] {
            const steps = ((this.process && this.process.items) || []).map((item: any, i: number) => ({ item, i }));
            const layout = this.node.ProcessLayout || null;
            if(layout != 'grid' || steps.length < 2) return [ { layout, steps } ];
            return [ { layout: null, steps: steps.slice(0, 1) }, { layout, steps: steps.slice(1) } ];
        },
        // Sign-offs / files / comments / history rail beside the content
        hasRail(): boolean {
            return [ 'PROCESS', 'TIMELINE', 'SHEET', 'CHECKLIST' ].includes(this.node.Type) && this.showAdminComponent && !this.previewNode;
        },
        typeCrumb(): string {
            const map: any = { PROCESS: 'Processes', TIMELINE: 'Value Engines', SHEET: 'Role Sheets', CHECKLIST: 'Powerlists', VIDEO: 'Videos' };
            return map[this.node.Type] || 'Back';
        },
        // Process, Powerlist, role sheet and value engine pages: the editor's Improve with AI, for Forge users
        canImprove(): boolean {
            return !this.previewNode && this.canEditNodes && !!this.editRoute && canUseForge(this.$store.state.user);
        },
        editRoute(): string {
            const map: any = { PROCESS: '/edit-process/', TIMELINE: '/edit-timeline/', SHEET: '/edit-sheet/', CHECKLIST: '/edit-checklist/' };
            return map[this.node.Type] ? map[this.node.Type] + this.node.Slug : '';
        },
        // "Process · Owner Diego Moncada · v25 · edited Sep 30"
        metaLine(): string {
            const parts = [ this.$store.state.playbookNodeTypeMap[this.node.Type] ];
            if(this.node.ProcessOwnerNames) parts.push('Owner ' + this.node.ProcessOwnerNames);
            if(this.node.Version != null) parts.push('v' + this.node.Version);
            if(this.node.LastModified) parts.push('edited ' + new Date(this.node.LastModified).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }));
            return parts.join(' · ');
        },
        reviewedToday(): boolean {
            return !!this.node.LastReviewed && new Date(this.node.LastReviewed).toDateString() == new Date().toDateString();
        }
    },
    async created() {
        await this.load();
    },
    methods: {
        improveWithAI() {
            this.$router.push({ path: this.editRoute, query: { ai: '1' } });
        },
        // Everything the page loads for its node. Also re-run in place (after a reset to fresh data) when Edit
        // Details saves, since the route, and so the router-view key, stays the same.
        async load() {
            if(!this.previewNode && !this.$route.params.node) {
                this.$store.state.routeLoaded = true;
                return;
            }

            if(this.previewNode) {
                this.node = JSON.parse(JSON.stringify(this.previewNode));
            } else if(this.$route.params.node == 'home') {
                this.node = {
                    Name: 'Home',
                    Type: 'NODE',
                    Slug: 'home'
                }
            } else {
                let error;
                let status = 0;
                // ?share= on an unpublished page's shared link lets standard users see it (API playbook.ts shareToken)
                const shareQuery = this.$route.query.share ? '?share=' + encodeURIComponent('' + this.$route.query.share) : '';
                await axios.get(`${this.$store.state.apiUrl}/api/playbook/node/${this.$route.params.node}${shareQuery}`)
                    .then(resp => {
                        this.node = resp.data;
                    })
                    .catch(e => {
                        console.log(e);
                        status = e?.response?.status || 0;
                        error = e?.response?.data || 'There was an error';
                    })

                // No access, or it's gone: say why on the page (the API's message names the reason), with
                // a way out. Anything else is unexpected, so the alert and back to Home.
                if(status == 403 || status == 404) {
                    this.blocked = { status, message: error };
                    this.canGoBack = window.history.length > 1;
                    this.$store.state.routeLoaded = true;
                    return;
                }
                if(error) {
                    alert(error);
                    this.$router.push('/playbook/home');
                    return;
                }
            }

            if(this.node.LocationsJSON) {
                this.locations = JSON.parse(this.node.LocationsJSON);
                this.locationString = this.locations.join(', ');
            }
            if(this.node.DepartmentsJSON) {
                this.departments = JSON.parse(this.node.DepartmentsJSON);
                this.departmentsString = this.departments.join(', ');
            }
            if(this.node.TypesJSON) {
                this.nodeTypes = JSON.parse(this.node.TypesJSON);
                this.nodeTypesString = this.nodeTypes.join(', ');
            }


            if(this.node.LastModified) {
                this.lastModified = new Date(this.node.LastModified).toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'short' });
            }

            if(this.node.LastReviewed) {
                this.lastReviewed = new Date(this.node.LastReviewed).toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'short' });
            }

            if(this.node.Slug) await this.getPrevNodes();

            if(this.node.Type == 'NODE') {
                await this.getNextNodes();
            } else if(this.node.Type == 'PROCESS') {
                this.process = JSON.parse(this.node.ProcessJSON);
            } else if(this.node.Type == 'TIMELINE') {
                this.timeline = JSON.parse(this.node.TimelineJSON);
            } else if(this.node.Type == 'SHEET') {
                this.sheet = readRoleSheet(this.node);
                const colID = !this.previewNode && this.$route.query.col ? `${this.$route.query.col}` : null;
                const employee = this.$store.state.user.teamHubEmployee;
                if(colID) {
                    if(this.sheet.seats.some((seat: any) => seat.id == colID)) {
                        this.initialSeatId = colID;
                    } else {
                        alert('Sheet Column does not exist');
                    }
                } else if(employee && employee.RoleSheetSlug == this.node.Slug) {
                    this.initialSeatId = employee.RoleSheetColID;
                }
            } else if(this.node.Type == 'CHECKLIST') {
                this.checklist = JSON.parse(this.node.ChecklistJSON);
                if(!this.checklist.weeklyItems) {
                    this.checklist.weeklyItems = [];
                }
            }

            if(this.node.Slug && (this.node.Type == 'PROCESS' || this.node.Type == 'SHEET' || this.node.Type == 'TIMELINE' || this.node.Type == 'CHECKLIST')) {
                await this.getFiles();
            }

            this.$store.state.routeLoaded = true;

            const table = document.getElementById('playbook-sheet-wrap');
            if(table) {
                this.doubleScroll(table);
            }
        },
        toggleFavorite() {
            this.$store.dispatch('toggleFavorite', 'node:' + this.node.Slug);
        },
        async getFiles() {
            await axios.get(`${this.$store.state.apiUrl}/api/playbook/files/${this.node.Slug}`)
                .then(resp => {
                    this.files = resp.data;
                })
                .catch(e => {
                    console.log(e);
                })
        },
        async getNextNodes() {
            await axios.get(`${this.$store.state.apiUrl}/api/playbook/nextnodes/${this.node.Slug}`)
                .then(resp => {
                    this.nextNodes = resp.data;

                    for(const node of this.nextNodes) {
                        node.Locations = JSON.parse(node.LocationsJSON);
                    }
                })
                .catch(e => {
                    console.log(e);
                })
        },
        async getPrevNodes() {
            await axios.get(`${this.$store.state.apiUrl}/api/playbook/prevnodes/${this.node.Slug}`)
                .then(resp => {
                    this.prevNodes = [];
                    for(const node of resp.data) {
                        if(node.ThisSlug == 'home') {
                            this.prevNodes.push({
                                ThisSlug: 'home',
                                Name: 'Home'
                            })
                        } else {
                            this.prevNodes.push({
                                ThisSlug: node.ThisSlug,
                                Name: node.Name
                            })
                        }
                    }
                })
                .catch(e => {
                    console.log(e);
                })
        },
        handleNode(node: any) {
            if(node.Type == 'LINK') {
                window.open(node.Link, '_blank');
                return;
            }
            this.$router.push('/playbook/' + node.Slug);
        },
        addEditNode(node: any) {
            this.duplicatingNode = false;
            this.nodeToEdit = node;
            this.showAddEdit = true;
        },
        // Same as Duplicate in Nodes.vue: a copy so editing the form doesn't change this page
        duplicateNode() {
            this.duplicatingNode = true;
            this.nodeToEdit = JSON.parse(JSON.stringify(this.node));
            this.showAddEdit = true;
        },
        // Same as Edit Details in Nodes.vue: name, locations, departments, owners and so on for this node.
        // A copy, so the form doesn't change this page until it's saved and reloaded.
        editDetails() {
            this.duplicatingNode = false;
            this.editingDetails = true;
            this.nodeToEdit = JSON.parse(JSON.stringify(this.node));
            this.showAddEdit = true;
        },
        // Reloads the whole page as if freshly opened (a changed type swaps the body too): fresh data, then load()
        // again, behind the app's loading state
        async reload() {
            this.$store.state.routeLoaded = false;
            Object.assign(this.$data, (this.$options.data as any).call(this));
            await this.load();
        },
        async closeAddEdit() {
            this.showAddEdit = false;
            this.duplicatingNode = false;
            if(this.editingDetails) {
                this.editingDetails = false;
                await this.reload();
                return;
            }

            this.getNextNodes();
        },
        closeAICreate() {
            this.showAICreate = false;
            this.getNextNodes();
        },
        async closeAddExisting() {
            this.showAddExisting = false;

            this.getNextNodes();
        },
        deleteNode(node: any) {
            this.nodeToDelete = node;
            this.showConfirmDelete = true;
        },
        async deleteNodeConfirm() {
            await axios.post(`${this.$store.state.apiUrl}/api/playbook/deletenode`, this.nodeToDelete)
                .catch(e => {
                    console.log(e?.response?.data || e);
                    alert('There was an error')
                })

            this.getNextNodes();
            this.$store.dispatch('setStoreData');

            this.showConfirmDelete = false;
        },
        removeNode(node: any) {
            this.nodeToRemove = node;
            this.showConfirmRemove = true;
        },
        async removeNodeConfirm() {
            this.nodeToRemove.fromSlug = this.node.Slug;
            await axios.post(`${this.$store.state.apiUrl}/api/playbook/removenode`, this.nodeToRemove)
                .catch(e => {
                    console.log(e?.response?.data || e);
                    alert('There was an error')
                })

            this.getNextNodes();

            this.showConfirmRemove = false;
        },
        async getPDF(wDescription: boolean, sharing?: boolean) {
            if(sharing && this.node.Type == 'VIDEO') {
                let query = '?z=t';
                if(!this.shareData.email && !this.shareData.phone) {
                    alert('Enter an email and/or phone to share this with');
                    return;
                }
                if(this.shareData.email) {
                    if(!this.shareData.email.includes('@') || !this.shareData.email.includes('.')) {
                        alert('Make sure to use a valid email');
                        return;
                    }
                    this.shareData.email = this.shareData.email.trim();
                    query += `&email=${encodeURIComponent(this.shareData.email)}`;
                }
                if(this.shareData.phone) {
                    const sharePhone = normalizePhone(this.shareData.phone);
                    if(!sharePhone) {
                        alert(PHONE_ERROR);
                        return;
                    }
                    query += `&phone=${encodeURIComponent(sharePhone)}`;
                }
                this.gettingPDF = true;
                await axios.get(`${this.$store.state.apiUrl}/api/playbook/sharevideo/${this.node.Slug}${query}`)
                    .then(resp => {
                        alert('Video has been shared!');
                    })
                    .catch(e => {
                        console.log(e?.response?.data || e);
                        alert(e?.response?.data || 'There was an error')
                    })
                    
                this.gettingPDF = false;
                this.showSharePDF = false;
                return;
            }

            if(this.gettingPDF) {
                return;
            }

            let pdfSlug = 'processpdf';
            if(this.node.Type == 'TIMELINE') {
                pdfSlug = 'timelinepdf';
            } else if(this.node.Type == 'SHEET') {
                pdfSlug = 'sheetpdf';
            } else if(this.node.Type == 'CHECKLIST') {
                pdfSlug = 'checklistpdf';
            }

            let query = '?z=t';
            if(sharing) {
                query += '&sharing=t';
                if(!this.shareData.email && !this.shareData.phone) {
                    alert('Enter an email and/or phone to share this with');
                    return;
                }
                if(this.shareData.email) {
                    if(!this.shareData.email.includes('@') || !this.shareData.email.includes('.')) {
                        alert('Make sure to use a valid email');
                        return;
                    }
                    this.shareData.email = this.shareData.email.trim();
                    query += `&email=${encodeURIComponent(this.shareData.email)}`;
                }
                if(this.shareData.phone) {
                    const sharePhone = normalizePhone(this.shareData.phone);
                    if(!sharePhone) {
                        alert(PHONE_ERROR);
                        return;
                    }
                    query += `&phone=${encodeURIComponent(sharePhone)}`;
                }                
                if(this.shareData.wDescriptions) {
                    query += '&wDescription=true'
                }
                if(this.shareData.roleSheetCol) {
                    query += `&roleSheetCol=${this.shareData.roleSheetCol}`
                }
            } else {
                if(wDescription) {
                    query += '&wDescription=true'
                }
            }

            this.gettingPDF = true;

            await axios.get(`${this.$store.state.apiUrl}/api/playbook/${pdfSlug}/${this.node.Slug}${query}`)
                .then(resp => {
                    if(sharing) {
                        alert('Document has been shared!');
                        this.showSharePDF = false;
                    } else {
                        this.PDFUrl = this.$store.state.apiUrl + '/temppdf/' + resp.data.fileName;
                        this.showPDF = true;
                    }
                })
                .catch(e => {
                    console.log(e?.response?.data || e);
                    alert(e?.response?.data || 'There was an error')
                })

            this.gettingPDF = false;
        },
        async getQRPDF() {
            if(this.gettingPDF) {
                return;
            }
            this.gettingPDF = true;
            await axios.get(`${this.$store.state.apiUrl}/api/playbook/qrpdf/${this.node.Slug}`)
                .then(resp => {
                    this.PDFUrl = this.$store.state.apiUrl + '/temppdf/' + resp.data.fileName;
                    this.showPDF = true;
                })
                .catch(e => {
                    console.log(e?.response?.data || e);
                    alert(e?.response?.data || 'There was an error')
                })
            this.gettingPDF = false;
        },
        // seatID: share just that seat of a role sheet
        sharePDF(seatID?: any) {
            this.shareData = {
                email: '',
                phone: '',
                wDescriptions: false,
                roleSheetCol: null
            }
            if(seatID) {
                this.shareData.roleSheetCol = seatID;
            }
            this.showSharePDF = true;
        },
        // Esc leaves Present, unless an image or page popup is open on top of it (Esc is for that first)
        onPresentKeydown(e: KeyboardEvent) {
            if(e.key == 'Escape' && this.presenting && !this.showImagePop && !this.showPDF) {
                this.presenting = false;
            }
        },
        openImagePop(url: string) {
            this.imagePopURL = url;
            this.showImagePop = true;
        },
        timelineLink(url: string) {
            if(url.includes('/playbook/')) {
                let link = url.replace('https://', '').replace('http://', '');
                link = link.split('?')[0];
                const firstSlashIndex = link.indexOf('/');
                this.PDFUrl = link.substring(firstSlashIndex, link.length) + '?hideNav=t';
                this.showPDF = true;
            } else {
                window.open(url, '_blank');
            }
        },
        async approveNode() {
            await axios.post(`${this.$store.state.apiUrl}/api/playbook/node/approve/${this.node.Slug}`)
                .then(resp => {
                    this.node.NeedsApproval = null;
                    alert(this.node.Name + ' has been approved!');
                })
                .catch(e => {
                    console.log(e?.response?.data || e);
                    alert('There was an error')
                })
        },
        async unapproveNode() {
            await axios.post(`${this.$store.state.apiUrl}/api/playbook/node/unapprove/${this.node.Slug}`)
                .then(resp => {
                    this.node.NeedsApproval = true;
                    alert(this.node.Name + ' has been marked unapproved');
                })
                .catch(e => {
                    console.log(e?.response?.data || e);
                    alert('There was an error')
                })
        },
        async markReviewed() {
            await axios.post(`${this.$store.state.apiUrl}/api/playbook/node/markreviewed/${this.node.Slug}`)
                .then(resp => {
                    this.$store.dispatch('setStoreData');
                    this.node.NeedsReview = null;
                    // flips the header button to "Reviewed today"
                    this.node.LastReviewed = new Date().toISOString();
                    this.node.LastReviewedBy = `${this.$store.state.user.firstName} ${this.$store.state.user.lastName}`;
                    this.lastReviewed = new Date().toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'short' });
                })
                .catch(e => {
                    console.log(e?.response?.data || e);
                    alert('There was an error')
                })
        },
        async toggleShowReorder() {
            if(this.showReorder) {
                this.showReorder = false;
                const data = {
                    fromSlug: this.node.Slug,
                    nextNodes: this.nextNodes
                }
                await axios.post(`${this.$store.state.apiUrl}/api/playbook/nextnodeorder`, data)
                    .catch(e => {
                        console.log(e?.response?.data || e);
                    })
            } else {
                this.showReorder = true;
            }
        },
        goBack() {
            window.history.back();
        },
        // Signs one seat of a role sheet; the sign-off keeps a snapshot of the seat (see seatSnapshot)
        signSeat(seat: any) {
            this.colSignOffCells = seatSnapshot(this.sheet, seat.id);
            this.showColSignOff = true;
        },
        async submitColSignOff() {
            if(!this.colSignOffInput) {
                alert('Type your name');
                return;
            }

            const { isEmpty, data } = (this.$refs as any).signOffPad.saveSignature();
            if(isEmpty) {
                alert('Include written signature');
                return;
            }

            this.colSignOffSubmitting = true;

            const formData = {
                cells: JSON.stringify(this.colSignOffCells),
                signature: this.colSignOffInput,
                slug: this.$route.params.node,
                nodeName: this.node.Name,
                signatureData: data
            }

            await axios.post(`${this.$store.state.apiUrl}/api/signoffs`, formData)
                .then(resp => {
                    alert('Sign off submitted!');
                    this.colSignOffInput = '';
                    (this.$refs as any).signOffPad.clearSignature();
                    this.showColSignOff = false;
                    this.showAdminComponent = false;
                    setTimeout(() => {
                        this.showAdminComponent = true;
                    }, 500)
                })
                .catch(e => {
                    console.log(e);
                    alert('There was an error');
                })

            this.colSignOffSubmitting = false;
        },
        copyLink() {
            let link = window.location.href.split('?')[0];
            // An unpublished page's link carries its share token, so standard users it's sent to can open it (the API sends it to admins)
            if(this.node.ShareToken) {
                link += '?share=' + encodeURIComponent(this.node.ShareToken);
            }
            navigator.clipboard.writeText(link);
        },
        toggleNextNodeMenu(next: any, event: MouseEvent) {
            if(this.openNextNodeSlug == next.Slug) {
                this.openNextNodeSlug = null;
                return;
            }

            const margin = 10;
            const dropdownWidth = 240;
            const btn = event.currentTarget as HTMLElement;
            const wrap = btn.closest('.next-node-card-wrap') as HTMLElement;
            const rect = btn.getBoundingClientRect();
            const wrapRect = wrap.getBoundingClientRect();

            const top = Math.min(rect.bottom + 6, window.innerHeight - margin);

            this.nextNodeMenuStyle = {
                top: (top - wrapRect.top) + 'px',
                left: '-10px',
                width: dropdownWidth + 'px',
                maxHeight: (window.innerHeight - top - margin) + 'px'
            };
            this.openNextNodeSlug = next.Slug;
        },
        closeNextNodeMenu() {
            this.openNextNodeSlug = null;
        },
        nextNodeMenuAction(action: any) {
            this.closeNextNodeMenu();
            action();
        },
        handleOutsideNextNodeMenuClick(e: MouseEvent) {
            if(this.openNextNodeSlug && !(e.target as HTMLElement).closest('.next-node-card-wrap')) {
                this.openNextNodeSlug = null;
            }
            if(this.showDetailMenu && !(e.target as HTMLElement).closest('.pb-more')) {
                this.showDetailMenu = false;
            }
        }
    },
    mounted() {
        document.addEventListener('click', this.handleOutsideNextNodeMenuClick);
        window.addEventListener('resize', this.closeNextNodeMenu);
        document.addEventListener('keydown', this.onPresentKeydown);
    },
    beforeUnmount() {
        document.removeEventListener('click', this.handleOutsideNextNodeMenuClick);
        window.removeEventListener('resize', this.closeNextNodeMenu);
        document.removeEventListener('keydown', this.onPresentKeydown);
    }
});
</script>
